/** React & Library Imports */
import {RBSButton, RBSButtonVariants} from "../../base/rbs_components/RBSButton";
import {RBSModal} from "../../base/rbs_components/RBSModal.tsx";
import Plot from "react-plotly.js";
import autobind from "autobind-decorator";
import styles from './TabShared.module.css';
/** Type Imports */
import type {TransactionSettings, Variable, WMT} from "../../base/ui_general/GeneralInterfaces.tsx";
import {
    FeedbackMessageTypes,
    DisplayFormatTypeEnum,
    LabelTextMessageTypes, DESIGN_CENTER_BOTTOM_OFFSET_PX,
} from "../../base/Settings.tsx";
import {
    BaseComponent,
    type BaseComponentState,
    BaseComponentStateInitial
} from "../../base/ui_general/BaseComponent.tsx";
import {RBSCard, RBSCardVariants} from "../../base/rbs_components/RBSCard";
import {
    createErrorFeedbackMessage,
    getLabelTextMessage,
    formatPercent,
    calcHeightToBottomOffset
} from "../../base/utils/GeneralUtilities.tsx";
import type {DesignCenterTabProps} from "../Interfaces.tsx";
import {createRef} from "react";


/** Props for WMTTab component */
interface WMTTabProps extends DesignCenterTabProps {
    wmt: WMT | undefined;
    variables: Array<Variable>;
    transaction_settings: TransactionSettings | undefined;
    onScenarioSelected: (scenarioNr: number) => void;
}


/** State for WMTTab component */
interface WMTTabState extends BaseComponentState {
    columnFilters: string[];
    manualPinnedColumns: string[];
    autoPinnedColumns: string[];
    manualPinnedRows: string[];
    startDate: string;
    endDate: string;
    isTransposed: boolean;
    plotData: Array<{ [key: string]: string | number }>;
    plotVariables: string[];
    showPlotModal: boolean;
    tableData: Array<Record<string, string>>;
    showScenarioModal: boolean;
    scenarioNrInput: string;
}

/** Component for displaying WMT table with filtering, pinning, and plotting */
export class WMTTab extends BaseComponent<WMTTabProps, WMTTabState> {
    private wmtPanelRef = createRef<HTMLDivElement>();

    constructor(props: WMTTabProps) {
        super(props);
        /** Initialize state with default values */
        this.state = {
            ...BaseComponentStateInitial,
            columnFilters: [""],
            manualPinnedColumns: [],
            autoPinnedColumns: [],
            manualPinnedRows: [],
            startDate: "",
            endDate: "",
            isTransposed: false,
            plotData: [],
            plotVariables: [],
            showPlotModal: false,
            tableData: [],
            showScenarioModal: false,
            scenarioNrInput: "0",
        };
    }

    /** Lifecycle: prepare table when component mounts */
    componentDidMount() {
        this.props.onScenarioSelected(Number(this.state.scenarioNrInput));
        this.prepareVariablesAndTable();
    }

    /** Lifecycle: react to prop changes */
    componentDidUpdate(prevProps: WMTTabProps) {
        if (prevProps.wmt !== this.props.wmt && this.props.wmt) {
            this.prepareVariablesAndTable();
        }
    }

    /** Prepare table data based on WMT and variable definitions */
    prepareVariablesAndTable() {
        if (!this.props.wmt) return;
        const {row_dates, column_names, values} = this.props.wmt;

        const tableData = row_dates.map((date, rowIndex) => {
            const row: Record<string, string> = {"Date": date};
            column_names.forEach((colName, colIndex) => {
                const variable = this.props.variables.find(v => v.name === colName);
                const value = values[rowIndex]?.[colIndex];

                if (variable && value !== undefined && value !== null) {
                    switch (variable.display_format) {
                        case DisplayFormatTypeEnum.AMOUNT:
                            row[colName] = `${value.toLocaleString("en-GB", {
                                minimumFractionDigits: 2,
                                maximumFractionDigits: 2
                            })} €`;
                            break;
                        case DisplayFormatTypeEnum.PERCENTAGE:
                            row[colName] = formatPercent(value);
                            break;
                        case DisplayFormatTypeEnum.DECIMAL_RATE:
                            row[colName] = Number(value).toLocaleString("en-GB", {
                                minimumFractionDigits: 4,
                                maximumFractionDigits: 4
                            });
                            break;
                        case DisplayFormatTypeEnum.INTEGER_NUMBER:
                            row[colName] = Number(value).toLocaleString("en-GB", {maximumFractionDigits: 0});
                            break;
                        case DisplayFormatTypeEnum.TIME:
                            try {
                                // Try to parse as date/time, fallback to string
                                const d = new Date(value);
                                row[colName] = isNaN(d.getTime()) ? value.toString() : d.toLocaleString();
                            } catch {
                                row[colName] = value.toString();
                            }
                            break;
                        default:
                            row[colName] = value.toString();
                    }
                } else if (value !== undefined && value !== null) {
                    row[colName] = value.toString();
                } else {
                    row[colName] = "---";
                }
            });
            return row;
        });

        this.setState({tableData: tableData});
    }

    /** Pin/unpin a column */
    @autobind
    togglePinColumn(col: string) {
        if (col === "Date" || this.state.isTransposed) return;
        this.setState(prev => ({
            manualPinnedColumns: prev.manualPinnedColumns.includes(col)
                ? prev.manualPinnedColumns.filter(c => c !== col)
                : [...prev.manualPinnedColumns, col],
        }));
    }

    /** Pin/unpin a row (variable) */
    @autobind
    togglePinRow(variableName: string) {
        this.setState(prev => ({
            manualPinnedRows: prev.manualPinnedRows.includes(variableName)
                ? prev.manualPinnedRows.filter(v => v !== variableName)
                : [...prev.manualPinnedRows, variableName],
        }));
    }

    /** Update column filter */
    @autobind
    updateFilter(index: number, value: string) {
        const newFilters = [...this.state.columnFilters];
        newFilters[index] = value;
        this.setState({columnFilters: newFilters});
    }

    /** Add new filter input */
    @autobind
    addFilter() {
        this.setState({columnFilters: [...this.state.columnFilters, ""]});
    }

    /** Remove a filter input */
    @autobind
    removeFilter(index: number) {
        if (index === 0) return;
        this.setState({columnFilters: this.state.columnFilters.filter((_, i) => i !== index)});
    }

    /** Get filtered table data based on date range */
    @autobind
    getFilteredTableData() {
        const {startDate, endDate} = this.state;

        const parseDate = (str: string) => {
            const [year, month, day] = str.split("-").map(Number);
            return new Date(year, month - 1, day);
        };

        const start = startDate ? new Date(startDate) : null;
        const end = endDate ? new Date(endDate) : null;

        return this.state.tableData.filter(
            row => {
                const rowDate = parseDate(row["Date"]);
                if (start && rowDate < start) return false;
                return !(end && rowDate > end);
            });
    }

    /** Transpose table data (rows ↔ columns) */
    @autobind
    transposeData(filteredData: Record<string, string>[]) {
        if (!this.props.wmt) return [];
        const {column_names} = this.props.wmt;

        return column_names.map(colName => {
            const row: Record<string, string> = {Variable: colName};
            filteredData.forEach(r => {
                row[r.Date] = r[colName];
            });
            return row;
        });
    }


    /** Prepare and show plot modal */
    @autobind
    handlePlot() {
        if (!this.props.wmt) return;

        let selectedVariables: Array<string>;
        if (this.state.isTransposed) {
            selectedVariables = this.state.manualPinnedRows;
        } else {
            selectedVariables = [
                ...this.state.manualPinnedColumns.filter(col => col !== "Date"),
                ...this.state.autoPinnedColumns.filter(col => col !== "Date"),
            ];
        }

        if (selectedVariables.length === 0) {
            this.props.displayMessage?.({
                message_type: FeedbackMessageTypes.WARNING,
                message_text: "Please pin at least one variable or column before plotting.",
            });
            return;
        }

        const filteredData = this.getFilteredTableData();
        const chartData = selectedVariables.map((varName, idx) => ({
            x: filteredData.map(row => row.Date),
            y: filteredData.map(row => Number(row[varName])),
            type: "scatter",
            mode: "lines+markers",
            name: varName,
            line: {width: 2, color: ["#007bff", "#28a745", "#ff5733", "#6f42c1"][idx % 4]},
        }));

        this.setState({
            //TODO: Fix the compiler warning here
            plotData: chartData,
            plotVariables: selectedVariables,
            showPlotModal: true,
        });
    }

    /** Close plot modal */
    @autobind
    closePlotModal() {
        this.setState({showPlotModal: false});
    }

    /** Confirm scenario selection */
    @autobind
    handleScenarioConfirm(): void {
        if (!this.props.transaction_settings) return;
        const scenarioNr = Number(this.state.scenarioNrInput);
        const maxSimulations = this.props.transaction_settings.number_of_simulations;

        if (isNaN(scenarioNr) || scenarioNr < 0 || scenarioNr > maxSimulations) {
            this.props.displayMessage?.(
                createErrorFeedbackMessage(LabelTextMessageTypes.INVALID_SCENARIO_NUMBER)
            );
            return;
        }
        this.props.onScenarioSelected(scenarioNr);
        this.setState({showScenarioModal: false});
    }

    /** Render scenario modal */
    renderScenarioModal() {
        return (
            <RBSModal
                show={this.state.showScenarioModal}
                header_title="Select Scenario"
                onClose={() => {
                }}
                primary_button_label="Confirm"
                onClickPrimary={this.handleScenarioConfirm}
                show_close_button={false}
            >
                <div>
                    <label>
                        {getLabelTextMessage(LabelTextMessageTypes.SCENARIO_NUMBER, this.props.user_language)}
                        <input
                            type="number"
                            min={0}
                            value={this.state.scenarioNrInput}
                            onChange={e => this.setState({scenarioNrInput: e.target.value})}
                            style={{marginLeft: "8px", padding: "4px 8px"}}
                        />
                    </label>
                </div>
            </RBSModal>
        );
    }

    /** Main render method */
    render() {
        const {wmt} = this.props;
        const scenarioModal = this.renderScenarioModal();

        if (!wmt) {
            return (
                <div ref={this.wmtPanelRef}>
                    {scenarioModal}
                    <RBSButton label={"Load WMT"} onClick={() => this.setState({showScenarioModal: true})}/>
                </div>
            );
        }

        const filteredTableData = this.getFilteredTableData();
        let visibleColumns: string[] = [];
        let tableToRender: Record<string, string>[];

        if (this.state.isTransposed) {
            tableToRender = this.transposeData(filteredTableData);
            const activeFilters = this.state.columnFilters.filter(f => f);
            if (activeFilters.length) {
                tableToRender = tableToRender.filter(row =>
                    activeFilters.some(filter => row.Variable.toLowerCase().includes(filter.toLowerCase()))
                );
            }
            visibleColumns = ["Variable", ...filteredTableData.map(r => r.Date)];
        } else {
            tableToRender = filteredTableData;

            const pinnedColumns = [...new Set([...this.state.autoPinnedColumns, ...this.state.manualPinnedColumns])];
            const filteredColumns = wmt.column_names.filter(col => !pinnedColumns.includes(col));
            visibleColumns = ["Date", ...pinnedColumns, ...filteredColumns];

            const activeFilters = this.state.columnFilters.filter(f => f);
            if (activeFilters.length) {
                const columnsToShow = wmt.column_names.filter(col =>
                    activeFilters.some(filter => col.toLowerCase().includes(filter.toLowerCase()))
                );
                visibleColumns = ["Date", ...columnsToShow];
            }
        }

        const heightToBottomOffset = calcHeightToBottomOffset(
            this.props.windowInnerHeight ?? 0,
            DESIGN_CENTER_BOTTOM_OFFSET_PX + 262,
            this.wmtPanelRef
        );

        return (
            <div ref={this.wmtPanelRef} style={{height: "100%", display: "flex", flexDirection: "column"}}>
                {scenarioModal}
                <RBSCard
                    header={<div className={styles.cardHeaderPrimaryInner}><span className={styles.cardHeaderTitleWrap}><span className={styles.cardHeaderIcon}>📊</span><span className={styles.cardHeaderTitle}>WMT Data</span></span></div>}
                    header_class_name={styles.cardHeaderPrimary}
                    background={RBSCardVariants.Light}
                    border={RBSCardVariants.Primary}
                    body_class_name={styles.cardBodyPanel}
                    container_class_name={styles.roundedCard}
                >
                    {/* Controls */}
                    <div style={{marginBottom: "12px", display: "flex", flexWrap: "wrap", gap: "12px"}}>
                        <RBSButton
                            label="Transpose Matrix"
                            variant={RBSButtonVariants.PRIMARY}
                            onClick={() => this.setState(prev => ({isTransposed: !prev.isTransposed}))}
                        />
                        <RBSButton
                            label="Plot Data"
                            variant={RBSButtonVariants.PRIMARY}
                            onClick={this.handlePlot}
                        />
                        <label>
                            Start Date:{" "}
                            <input
                                type="date"
                                value={this.state.startDate}
                                onChange={e => this.setState({startDate: e.target.value})}
                                style={{
                                    padding: "6px 12px",
                                    borderRadius: "8px",
                                    border: "1px solid #ccc",
                                    outline: "none"
                                }}
                            />
                        </label>
                        <label>
                            End Date:{" "}
                            <input
                                type="date"
                                value={this.state.endDate}
                                onChange={e => this.setState({endDate: e.target.value})}
                                style={{
                                    padding: "6px 12px",
                                    borderRadius: "8px",
                                    border: "1px solid #ccc",
                                    outline: "none"
                                }}
                            />
                        </label>
                    </div>

                    {/* Column Filters */}
                    <div style={{
                        display: "flex",
                        flexWrap: "wrap",
                        gap: "8px",
                        alignItems: "center",
                        marginBottom: "12px"
                    }}>
                        {this.state.columnFilters.map((filter, idx) => (
                            <div key={idx} style={{position: "relative"}}>
                                <input
                                    type="text"
                                    placeholder={`Filter ${this.state.isTransposed ? "variable" : "column"} ${idx + 1}`}
                                    value={filter}
                                    onChange={e => this.updateFilter(idx, e.target.value)}
                                    style={{
                                        padding: "6px 36px 6px 12px",
                                        width: "200px",
                                        borderRadius: "8px",
                                        border: "1px solid #ccc",
                                        outline: "none"
                                    }}
                                />
                                {idx > 0 && (
                                    <button
                                        onClick={() => this.removeFilter(idx)}
                                        style={{
                                            position: "absolute",
                                            right: "6px",
                                            top: "50%",
                                            transform: "translateY(-50%)",
                                            border: "none",
                                            background: "transparent",
                                            cursor: "pointer",
                                            fontSize: "16px"
                                        }}
                                        title="Remove filter"
                                    >
                                        🗑️
                                    </button>
                                )}
                            </div>
                        ))}
                        <RBSButton label="+ Add filter" variant={RBSButtonVariants.PRIMARY} onClick={this.addFilter}/>
                    </div>

                    {/* Scrollable Table */}
                    <div style={{
                        flex: 1,
                        minHeight: 0,
                        height: heightToBottomOffset,
                        overflowY: "auto",
                        border: "1px solid #dee2e6",
                        borderRadius: "8px"
                    }}>
                        <table className="table table-bordered table-hover"
                               style={{margin: 0, minWidth: "max-content"}}>
                            <thead>
                            <tr>
                                {visibleColumns.map((key, idx) => (
                                    <th
                                        key={`${key}-${idx}`}
                                        style={{
                                            textAlign: "center",
                                            padding: "8px",
                                            position: "sticky",
                                            top: 0,
                                            left: idx === 0 ? 0 : undefined,
                                            backgroundColor: this.state.manualPinnedColumns.includes(key) ? "#e0f7fa" : "white",
                                            zIndex: idx === 0 ? 5 : 4,
                                            maxWidth: idx === 0 ? "200px" : "auto",
                                            whiteSpace: idx === 0 ? "normal" : "nowrap",
                                            wordWrap: idx === 0 ? "break-word" : "normal",
                                        }}
                                        onClick={() => key !== "Date" && this.togglePinColumn(key)}
                                    >
                                        {key}
                                    </th>
                                ))}
                            </tr>
                            </thead>
                            <tbody>
                            {tableToRender.map((row, rowIdx) => (
                                <tr key={`${row.Variable ?? rowIdx}-${rowIdx}`}>
                                    {visibleColumns.map((col, colIdx) => (
                                        <td
                                            key={`${col}-${colIdx}`}
                                            style={{
                                                padding: "8px",
                                                textAlign: colIdx === 0 ? "left" : "right",
                                                position: colIdx === 0 ? "sticky" : "relative",
                                                left: colIdx === 0 ? 0 : "auto",
                                                zIndex: colIdx === 0 ? 1 : 0,
                                                backgroundColor: colIdx === 0
                                                    ? this.state.manualPinnedRows.includes(row.Variable ?? "")
                                                        ? "#e0f7fa"
                                                        : "#f8f9fa"
                                                    : "none",
                                                maxWidth: colIdx === 0 ? "200px" : "auto",
                                                whiteSpace: colIdx === 0 ? "normal" : "nowrap",
                                                wordWrap: colIdx === 0 ? "break-word" : "normal"
                                            }}
                                            onClick={() => col === "Variable" && this.togglePinRow(row.Variable ?? "")}
                                        >
                                            {row[col]}
                                        </td>
                                    ))}
                                </tr>
                            ))}
                            </tbody>
                        </table>
                    </div>

                    {/* Plot Modal */}
                    <RBSModal
                        show={this.state.showPlotModal}
                        onClose={this.closePlotModal}
                        header_title="Plot Variables"
                        primary_button_label="Close"
                        onClickPrimary={this.closePlotModal}
                        show_close_button={true}
                    >
                        {this.state.plotData.length > 0 && (
                            <div style={{height: 400}}>
                                <h5>Plot: {this.state.plotVariables.join(", ")}</h5>
                                <Plot
                                    data={this.state.plotData}
                                    layout={{
                                        autosize: true,
                                        margin: {t: 40, r: 20, l: 50, b: 50},
                                        legend: {orientation: "h", x: 1, xanchor: "right", y: 1.1},
                                        xaxis: {title: {text: "Date"}},
                                        yaxis: {title: {text: "Value"}},
                                    }}
                                    style={{width: "100%", height: "100%"}}
                                    useResizeHandler={true}
                                />
                            </div>
                        )}
                    </RBSModal>
                </RBSCard>
            </div>
        );
    }
}
